import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/layout/AuthShell";
import { TotpRequiredError, verifyTotpChallenge, startExternalAuthentication, type ExternalAuthProvider } from "@/api/auth";
import { canUseRequiredCookies } from "./required-cookie";
import { nativeUpdateAuthenticationReturnTo } from "@/api/local-gateway-update";
import googleLogo from "@/assets/auth/google-g.png";
import microsoftLogo from "@/assets/auth/microsoft.svg";

type LoginView = "method_selection" | "local_credentials";
type PendingAction = "local" | ExternalAuthProvider;

export function LoginPage() {
  const { t } = useTranslation("auth");
  const { login, refreshMe, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState =
    location.state as
      | {
          prefillEmail?: string;
          from?: {
            pathname: string;
            search?: string;
            hash?: string;
          };
        }
      | null;
  const prefillEmail = locationState?.prefillEmail;

  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [email, setEmail] = useState(prefillEmail ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loginView, setLoginView] = useState<LoginView>("method_selection");
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const inFlightRef = useRef(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordMethodButtonRef = useRef<HTMLButtonElement>(null);
  const restoreMethodFocusRef = useRef(false);

  const from = locationState?.from
    ? `${locationState.from.pathname}${locationState.from.search ?? ""}${locationState.from.hash ?? ""}`
    : "/console/dashboard";

  useEffect(() => {
    if (loginView === "local_credentials") {
      emailInputRef.current?.focus();
      return;
    }

    if (restoreMethodFocusRef.current) {
      restoreMethodFocusRef.current = false;
      passwordMethodButtonRef.current?.focus();
    }
  }, [loginView]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (inFlightRef.current) {
      return;
    }

    setError("");

    if (!totpRequired && (!email || !password)) {
      setError(t("validation.required"));
      return;
    }

    if (!canUseRequiredCookies()) {
      setError(t("error.requiredCookieUnavailable"));
      return;
    }

    inFlightRef.current = true;
    setPendingAction("local");
    try {
      if (totpRequired) {
        const result = await verifyTotpChallenge(useRecoveryCode ? { recoveryCode: totpCode } : { code: totpCode });
        if (!("accountId" in result)) throw new Error("unexpected_authentication_result");
        await refreshMe();
      } else await login({ email, password });
      navigate(from, { replace: true });
    } catch (cause) {
      if (cause instanceof TotpRequiredError) { setTotpRequired(true); setPassword(""); }
      else setError(totpRequired ? "コードを確認してください。期限切れの場合はログインを最初からやり直してください。" : t("error.invalidCredentials"));
      setTotpCode("");
    } finally {
      inFlightRef.current = false;
      setPendingAction(null);
    }
  };

  const handleExternalLogin = async (provider: ExternalAuthProvider) => {
    if (inFlightRef.current) {
      return;
    }

    setError("");

    if (!canUseRequiredCookies()) {
      setError(t("error.requiredCookieUnavailable"));
      return;
    }

    inFlightRef.current = true;
    setPendingAction(provider);
    try {
      const nativeReturnTo = nativeUpdateAuthenticationReturnTo(from);
      if (nativeReturnTo) sessionStorage.setItem("kdrive.auth.return", nativeReturnTo);
      else sessionStorage.removeItem("kdrive.auth.return");
      await startExternalAuthentication(provider, { intent: "login" });
    } catch {
      sessionStorage.removeItem("kdrive.auth.return");
      setError(t("error.loginFailed"));
      inFlightRef.current = false;
      setPendingAction(null);
    }
  };

  const handleSelectPassword = () => {
    if (inFlightRef.current) {
      return;
    }

    setError("");
    setLoginView("local_credentials");
  };

  const handleReselectMethod = () => {
    if (inFlightRef.current) {
      return;
    }

    setPassword("");
    setError("");
    restoreMethodFocusRef.current = true;
    setLoginView("method_selection");
  };

  const isPending = pendingAction !== null;
  const isMethodSelection = loginView === "method_selection";

  return (
    <AuthShell
      title={isMethodSelection ? t("title") : t("passwordLoginTitle")}
      description={
        isMethodSelection ? t("description") : t("passwordLoginDescription")
      }
    >
      <div className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        {totpRequired ? <form className="space-y-4" onSubmit={handleSubmit}>
          <Label htmlFor="login-totp">{useRecoveryCode ? "回復コード" : "認証アプリの6桁コード"}</Label>
          <Input id="login-totp" autoComplete="one-time-code" value={totpCode} onChange={e => setTotpCode(e.target.value)} autoFocus disabled={isPending} />
          <Button className="w-full" disabled={isPending || !totpCode}>ログイン</Button>
          <Button type="button" variant="ghost" disabled={isPending} onClick={() => { setUseRecoveryCode(!useRecoveryCode); setTotpCode(""); }}>{useRecoveryCode ? "認証アプリを使う" : "回復コードを使う"}</Button>
          <Button type="button" variant="ghost" disabled={isPending} onClick={() => { setTotpRequired(false); setTotpCode(""); setLoginView("method_selection"); }}>ログインをやり直す</Button>
        </form> : isMethodSelection ? (
          <div className="space-y-3">
            <Button
              ref={passwordMethodButtonRef}
              type="button"
              variant="outline"
              size="lg"
              className="relative w-full justify-center"
              disabled={isPending}
              onClick={handleSelectPassword}
            >
              <KeyRound aria-hidden="true" className="absolute left-4 h-5 w-5" />
              {t("passwordLoginMethod")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="relative w-full justify-center"
              disabled={isPending}
              aria-describedby="external-account-notice"
              onClick={() => void handleExternalLogin("google")}
            >
              <img
                src={googleLogo}
                alt=""
                aria-hidden="true"
                className="absolute left-4 h-5 w-auto"
              />
              {pendingAction === "google"
                ? t("redirectingToGoogle")
                : t("googleLoginMethod")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="relative w-full justify-center"
              disabled={isPending}
              aria-describedby="external-account-notice"
              onClick={() => void handleExternalLogin("microsoft")}
            >
              <img
                src={microsoftLogo}
                alt=""
                aria-hidden="true"
                className="absolute left-4 h-5 w-5"
              />
              {pendingAction === "microsoft"
                ? t("redirectingToMicrosoft")
                : t("microsoftLoginMethod")}
            </Button>
            <p
              id="external-account-notice"
              className="pt-1 text-sm leading-6 text-muted-foreground"
            >
              {t("externalAccountNotice")}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                ref={emailInputRef}
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("passwordPlaceholder")}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isPending}>
              {pendingAction === "local" ? t("loggingIn") : t("loginButton")}
            </Button>

            <div className="space-y-1 text-center">
              <Button
                type="button"
                variant="link"
                className="h-auto p-1"
                disabled={isPending}
                onClick={() => navigate("/auth/local-recovery/request")}
              >
                {t("passwordLoginHelp")}
              </Button>
              <div>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-1 text-muted-foreground"
                  disabled={isPending}
                  onClick={handleReselectMethod}
                >
                  {t("reselectLoginMethod")}
                </Button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-3 border-t pt-5">
          <div className="text-center text-sm text-muted-foreground">
            <span>{t("signupPrompt")} </span>
            <Link to="/signup" className="font-medium text-primary underline">
              {t("signupLink")}
            </Link>
          </div>
          <div className="text-center text-sm">
            <Link
              to="/contact"
              className="text-muted-foreground underline hover:text-foreground"
            >
              {t("contact")}
            </Link>
          </div>
        </div>
      </div>
    </AuthShell>
  );
}
